import type { Feed } from '@knocklabs/client'
import { useCallback, useEffect, useRef, useState } from 'react'

const RECONCILE_COOLDOWN_MS = 5_000
const RECONCILE_INTERVAL_MS = 90_000

interface UseInboxFeedReconcileResult {
  pendingRefresh: boolean
  reconcile: () => void
}

/*
 * Reconcile the inbox feed on route entry, tab refocus and a gentle interval:
 * a no-arg feedClient.fetch() replaces the store with a fresh archived:exclude
 * page, so items archived elsewhere drop and new items arrive.
 *
 * While a detail panel is open, refocus and the interval do nothing here. A
 * full replace collapses the list to page one and moves the row being read;
 * removals in that state come from the app-wide coordinator, which prunes
 * surgically and never touches the open row. Route entry is the exception —
 * the user just arrived and is not mid-read — so it always refetches.
 *
 * Without the coordinator there is nothing pruning behind the open panel, so
 * the caller is told a refresh is pending and offers it manually. That is the
 * off-state behaviour the flag has to be able to return to.
 */
export function useInboxFeedReconcile({
  feedClient,
  isEngaged,
  liveRemovalEnabled = false,
  onReplaced,
  protectedItemId,
}: {
  feedClient: Feed
  isEngaged: boolean
  liveRemovalEnabled?: boolean
  /* Told when the full-replace fetch has landed, so a coordinator page read
     before it is not applied on top of it. */
  onReplaced?: (at: number) => void
  /* The row open in the detail panel, if any. A full-replace fetch() can drop
     it just by it not being on the fresh page — the same failure live removal
     is built to avoid — so it is snapshotted and restored. */
  protectedItemId?: string
}): UseInboxFeedReconcileResult {
  const [pendingRefresh, setPendingRefresh] = useState(false)
  const lastRef = useRef(0)
  /* Read CURRENT isEngaged inside the listener/interval — do not close over the prop. */
  const isEngagedRef = useRef(isEngaged)
  isEngagedRef.current = isEngaged
  const feedClientRef = useRef(feedClient)
  feedClientRef.current = feedClient
  const liveRemovalRef = useRef(liveRemovalEnabled)
  liveRemovalRef.current = liveRemovalEnabled
  const protectedItemIdRef = useRef(protectedItemId)
  protectedItemIdRef.current = protectedItemId
  const onReplacedRef = useRef(onReplaced)
  onReplacedRef.current = onReplaced

  const reconcile = useCallback(() => {
    /* Reset the cooldown so a refocus right after a refetch doesn't refetch again. */
    lastRef.current = Date.now()
    setPendingRefresh(false)
    const client = feedClientRef.current
    /* The restored copy may itself be archived by now, which is accepted: it
       is the open row, and it leaves when closed. */
    const before = client.store.getState().items
    void client.fetch().then(() => {
      onReplacedRef.current?.(Date.now())
      /* Which row is protected is read after the fetch, not before it. A
         selection change in flight must not restore the row the user has
         already left, and must restore the one they moved to — they picked it
         off the pre-fetch list, so the snapshot still holds it. */
      const protectedId = liveRemovalRef.current
        ? protectedItemIdRef.current
        : undefined
      const protectedItem = protectedId
        ? before.find((item) => item.id === protectedId)
        : undefined
      if (!protectedId || !protectedItem) {
        return
      }
      client.store.setState((state) => {
        if (state.items.some((item) => item.id === protectedId)) {
          return state
        }
        return { ...state, items: [protectedItem, ...state.items] }
      })
    })
  }, [])

  const maybeReconcile = useCallback(() => {
    if (Date.now() - lastRef.current < RECONCILE_COOLDOWN_MS) {
      return
    }
    if (isEngagedRef.current) {
      /* No fetch happened, so the cooldown is deliberately not spent. */
      if (!liveRemovalRef.current) {
        setPendingRefresh(true)
      }
      return
    }
    reconcile()
  }, [reconcile])

  /* Navigating within Orbit back into the inbox remounts this hook but not the
     Knock provider, so without this the route shows whatever the store held
     when the user left. Unconditional on engagement: arriving with a panel
     already open (a deep link, browser back) is not mid-read, and the open row
     is protected by the restore above. */
  useEffect(() => {
    if (!liveRemovalRef.current) {
      return
    }
    reconcile()
  }, [reconcile])

  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        maybeReconcile()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        maybeReconcile()
      }
    }, RECONCILE_INTERVAL_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      clearInterval(interval)
    }
  }, [maybeReconcile])

  return { pendingRefresh, reconcile }
}
